import { Controller } from '@hotwired/stimulus';
import { WebViewEvent, checkIfWebView } from '@infakt/webview-toolkit';
import Pinlogin from '../../../../application/pinlogin';
import { sanitizeHtml } from '../../../../shared/utils/htmlSanitizer';

export default class extends Controller {
  static NUMBER_OF_FIELDS = 6;
  static INPUT_CLASS_NAME =
    'peer m-0 block h-[56px] w-full rounded-lg border border-solid border-gray-300 bg-transparent bg-clip-padding font-normal leading-tight placeholder:text-transparent focus:text-neutral-700 focus:outline-nonedark:border-neutral-400 dark:autofill:shadow-autofill focus:border-primary-600 focus-visible:outline-none hover:bg-gray-50 focus:bg-inherit transition-colors duration-200 ease-in-out text-center p-0 md:px-3 md:py-4';

  static targets = [
    'obrAgreementCheckbox',
    'bankingTermsAgreementCheckbox',
    'bankingAgreementCheckbox',
    'bankingConsentCheckbox',
    'advisorTermsCheckbox',
    'advisorConsentCheckbox',
    'checkAll',
    'formContainer',
    'pinlogin',
    'otpForm',
    'consentDetails',
    'toggleText',
    'advisorConsentDetails',
    'advisorConsentToggle',
  ];

  static values = {
    otpFormPath: String,
  };

  get consentCheckboxes() {
    const checkboxes = [
      this.obrAgreementCheckboxTarget,
      this.bankingTermsAgreementCheckboxTarget,
      this.bankingAgreementCheckboxTarget,
      this.bankingConsentCheckboxTarget,
    ];

    if (this.hasAdvisorTermsCheckboxTarget) {
      checkboxes.push(this.advisorTermsCheckboxTarget);
    }

    if (this.hasAdvisorConsentCheckboxTarget) {
      checkboxes.push(this.advisorConsentCheckboxTarget);
    }

    return checkboxes;
  }

  connect() {
    if (this.hasCheckAllTarget) {
      this.checkAllTarget.addEventListener('change', this.handleCheckAllChange);
      this.consentCheckboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', this.handleCheckboxChange);
      });
    }
  }

  disconnect() {
    if (this.hasCheckAllTarget) {
      this.checkAllTarget.removeEventListener('change', this.handleCheckAllChange);
      this.consentCheckboxes.forEach((checkbox) => {
        checkbox.removeEventListener('change', this.handleCheckboxChange);
      });
    }
  }

  handleCheckAllChange = (event) => {
    const isChecked = event.target.checked;
    this.consentCheckboxes.forEach((checkbox) => {
      checkbox.checked = isChecked;
    });
  };

  handleCheckboxChange = () => {
    const allChecked = this.consentCheckboxes.every((checkbox) => checkbox.checked);

    this.checkAllTarget.checked = allChecked;
  };

  sendDownloadFileWebviewEvent(event) {
    if (!checkIfWebView()) {
      return;
    }

    event.preventDefault();

    new WebViewEvent({
      event: 'DownloadFile',
      params: {
        url: event.target.href,
      },
    }).dispatch();
  }

  showAgreements(event) {
    event.preventDefault();
    window.location.reload();
  }

  toggleConsentDetails(event) {
    event.preventDefault();
    if (this.hasConsentDetailsTarget && this.hasToggleTextTarget) {
      const isHidden = this.consentDetailsTarget.classList.contains('hidden');
      this.consentDetailsTarget.classList.toggle('hidden');
      this.toggleTextTarget.textContent = isHidden ? '(zwiń)' : '(więcej)';
    }
  }

  toggleAdvisorConsent(event) {
    event.preventDefault();
    if (this.hasAdvisorConsentDetailsTarget && this.hasAdvisorConsentToggleTarget) {
      const isHidden = this.advisorConsentDetailsTarget.classList.contains('hidden');
      this.advisorConsentDetailsTarget.classList.toggle('hidden');
      this.advisorConsentToggleTarget.textContent = isHidden ? '(zwiń)' : '(więcej)';
    }
  }

  sendOpenBrowserWebviewEvent(event) {
    if (!checkIfWebView()) {
      return;
    }

    event.preventDefault();

    new WebViewEvent({
      event: 'OpenBrowser',
      params: {
        url: event.target.href,
      },
    }).dispatch();
  }

  validateForm = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.hasObrAgreementCheckboxTarget) {
      const targets = this.consentCheckboxes;

      let hasUnchecked = false;

      targets.forEach((checkbox) => {
        if (!checkbox.checked) {
          checkbox.classList.add('border-red-600');
          hasUnchecked = true;
        } else {
          checkbox.classList.remove('border-red-600');
        }
      });

      if (hasUnchecked) {
        notyf.open({
          type: 'error',
          message: 'Zaakceptuj wszystkie zgody aby kontynuować.',
        });
        return false;
      }
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    fetch(this.otpFormPathValue, {
      headers: {
        Accept: 'text/html',
        'X-CSRF-Token': csrfToken,
      },
    })
      .then((response) => {
        return response.text();
      })
      .then((htmlResponse) => {
        const formContainer = document.getElementById('banking_agreement_form_container');
        if (formContainer) {
          formContainer.innerHTML = sanitizeHtml(htmlResponse);
        }
      })
      .catch((error) => {
        console.error('Błąd podczas żądania fetch:', error);
      });

    return false;
  };

  pinloginTargetConnected() {
    const pinloginElement = document.querySelector('#x-pinlogin');
    if (!pinloginElement) {
      console.error('Nie znaleziono elementu #x-pinlogin');
      return;
    }

    const pinlogin = this.loadPinForm();
    this.applyStyles(pinlogin);
  }

  onFormCompleted(value) {
    const codeField = document.querySelector('.x-bank-otp-form-code-field');
    const submitButton = document.querySelector('.x-bank-otp-submit');

    if (codeField && submitButton) {
      codeField.value = value;
      submitButton.disabled = false;

      if (checkIfWebView()) {
        document.activeElement?.blur();
      } else {
        submitButton.focus();
      }
    }
  }

  loadPinForm() {
    return new Pinlogin(document.querySelector('#x-pinlogin'), {
      fields: this.constructor.NUMBER_OF_FIELDS,
      reset: false,
      copypaste: true,
      hideinput: false,
      pattern: '^\\d$',
      keydown: (e, field, nr) => {
        document.querySelector('.x-bank-otp-submit').disabled = true;
      },
      complete: (pin) => this.onFormCompleted(pin),
    });
  }

  applyFieldStyles(field) {
    field.className = this.constructor.INPUT_CLASS_NAME;
  }

  resendOtpCode(event) {
    event.preventDefault();

    const url = event.target.dataset.url;
    const successMessage = event.target.dataset.otpResentSuccessMessage;
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/html',
        'X-CSRF-Token': csrfToken,
      },
    })
      .then((response) => response.text())
      .then((htmlResponse) => {
        const formContainer = document.getElementById('banking_agreement_form_container');
        if (formContainer) {
          formContainer.innerHTML = sanitizeHtml(htmlResponse);
        }
        if (successMessage) {
          notyf.open({ type: 'success', message: successMessage });
        }
      })
      .catch((error) => {
        console.error('Błąd podczas żądania fetch (resendOtpCode):', error);
      });
  }

  submitOtpForm(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const formData = new FormData(form);
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    fetch(form.action, {
      method: 'POST',
      headers: {
        Accept: 'text/html',
        'X-CSRF-Token': csrfToken,
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 201) {
          window.location.href = form.getAttribute('data-redirect-url');
          return null;
        } else {
          return response.text();
        }
      })
      .then((htmlResponse) => {
        if (htmlResponse) {
          const formContainer = document.getElementById('banking_agreement_form_container');
          if (formContainer) {
            formContainer.innerHTML = sanitizeHtml(htmlResponse);
          }
        }
      })
      .catch((error) => {
        console.error('Błąd podczas żądania fetch (submitOtpForm):', error);
      });
  }

  applyStyles(component) {
    const submitButton = document.querySelector('.x-bank-otp-submit');
    if (submitButton) {
      submitButton.disabled = true;
    }

    Array.from({ length: this.constructor.NUMBER_OF_FIELDS }).forEach((_, index) => {
      const field = component.getField(index);
      this.applyFieldStyles(field);
      field.classList.add('one-time-code');
    });

    const pinloginContainer = document.querySelector('#x-pinlogin_pinlogin');
    if (pinloginContainer) {
      pinloginContainer.classList.add('flex', 'gap-2');
    }
  }
}
